/**
 * SidePanel — 우측 패널 셸. ResultsPanel·FilePreviewPanel 이 통째로 복사해 쓰던
 * 프레임(aside + 좌측 엣지 너비 드래그 + 헤더)을 한 곳으로.
 *
 * 기존 rp-* 클래스를 그대로 소비한다 — 이 치환으로 화면이 1px 도 바뀌지 않는다.
 *
 * 너비는 --rp-w 변수 하나로 움직이고 localStorage 'va_rp_w' 에 남는다.
 * 결과·미리보기가 같은 키를 쓰는 것은 의도다 — 같은 자리(.main 2열)를 나눠 쓰는
 * 한 패널이라, 탭을 오가도 폭이 튀면 안 된다.
 */
import { useEffect, useRef, type ReactNode, type Ref, type MutableRefObject } from "react";
import { Button } from "./Button";

const WIDTH_KEY = "va_rp_w";

function clampW(px: number): number {
  const max = Math.min(980, Math.floor(window.innerWidth * 0.62));
  return Math.max(380, Math.min(max, px));
}

export interface SidePanelProps {
  /** aside aria-label — "결과 패널" / "파일 미리보기" */
  label: string;
  className?: string;
  /** 패널 rect 가 필요한 쪽(팝오버 위치 계산 등)이 받아 간다 — React 19 ref prop */
  ref?: Ref<HTMLElement>;
  /** 통합 패널 탭 바 — 결과·미리보기 둘 다 있을 때만 ChatPage 가 내려준다 */
  tabs?: ReactNode;
  /** rp-titles 내용 — 제목+부제목. 세트 선택 드롭다운(rp-picker)도 가능 */
  titles: ReactNode;
  /** 헤더 우측 액션들. 닫기(PanelClose)도 여기로 — 감싸는 구조가 패널마다 달라서 */
  actions?: ReactNode;
  /** 너비 드래그 중 상태 — .chat-root.rp-resizing (커서·가이드라인) */
  onResizingChange: (on: boolean) => void;
  children: ReactNode;
}

/**
 * 채팅 화면 오른쪽에 뜨는 와이드 패널의 **공용 프레임**. 스크리닝 결과 표
 * (ResultsPanel)와 파일 미리보기(FilePreviewPanel)가 이 껍데기를 같이 쓴다.
 * 프레임이 하는 일: 카드 모양 aside + 좌측 엣지 너비 드래그(localStorage 기억) +
 * 헤더(제목·부제목·우측 액션). 몸통(표·iframe)은 children 으로 받는다.
 */
export function SidePanel({
  label,
  className,
  ref,
  tabs,
  titles,
  actions,
  onResizingChange,
  children,
}: SidePanelProps) {
  const innerRef = useRef<HTMLElement | null>(null);
  const setRef = (el: HTMLElement | null) => {
    innerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) (ref as MutableRefObject<HTMLElement | null>).current = el;
  };

  // 저장된 패널 너비 복원
  useEffect(() => {
    try {
      const saved = Number(localStorage.getItem(WIDTH_KEY));
      if (saved && innerRef.current)
        innerRef.current.style.setProperty("--rp-w", clampW(saved) + "px");
    } catch {
      /* noop */
    }
  }, []);

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    onResizingChange(true);
    const move = (ev: MouseEvent) => {
      const w = clampW(window.innerWidth - ev.clientX - 12); // 우측 여백 12px 보정
      innerRef.current?.style.setProperty("--rp-w", w + "px");
      try {
        localStorage.setItem(WIDTH_KEY, String(w));
      } catch {
        /* noop */
      }
    };
    const up = () => {
      onResizingChange(false);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  return (
    <aside
      className={["results-panel", className].filter(Boolean).join(" ")}
      ref={setRef}
      aria-label={label}
    >
      <div className="rp-resizer" title="너비 조절" onMouseDown={startResize} />
      {tabs}
      <div className="rp-head">
        <div className="rp-titles">{titles}</div>
        {actions}
      </div>
      {children}
    </aside>
  );
}

/** 패널 닫기 — 두 패널이 같은 X 아이콘 svg 를 복사해 쓰던 것을 공용으로 */
export function PanelClose({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button variant="icon" className="rp-close" title="닫기" aria-label={label} onClick={onClick}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6L6 18" />
        <path d="M6 6l12 12" />
      </svg>
    </Button>
  );
}
